import { create } from 'zustand';
import {
    sendLines,
    sendLineUpsert,
    sendLineRemove,
    sendLinesFilters,
    sendMetrosTotalRota,
    sendMetrosTotalRotaUpdated,
    sendMetrosTotalRotaDeleted
} from '../channels/linesChannel';

import { sentidoOptions } from '../utils/sentidoUtils';

export function getLinhasFiltradas({
    linhasDisponiveis,
    allowClientes,
    allowPerfis,
    sentidosSet
}) {

    return linhasDisponiveis.filter(linha => {

        if (
            allowClientes &&
            !allowClientes.has(
                String(linha.client_id)
            )
        ) {
            return false;
        }

        if (
            allowPerfis &&
            !allowPerfis.has(
                Number(linha.unidade_opera)
            )
        ) {
            return false;
        }

        if (
            sentidosSet &&
            !sentidosSet.has(linha.SENTIDO)
        ) {
            return false;
        }

        return true;
    });

}

export function getLinhasSelecionadasEfetivas({
    linhasDisponiveis,
    linhasSelecionadas,
    allowClientes,
    allowPerfis,
    sentidosSet
}) {

    if (linhasSelecionadas !== null) {
        return linhasSelecionadas;
    }

    return linhasDisponiveis
        .filter(linha => {

            if (
                allowClientes &&
                !allowClientes.has(
                    String(linha.client_id)
                )
            ) {
                return false;
            }

            if (
                allowPerfis &&
                !allowPerfis.has(
                    Number(linha.unidade_opera)
                )
            ) {
                return false;
            }

            if (
                sentidosSet &&
                !sentidosSet.has(linha.SENTIDO)
            ) {
                return false;
            }

            return true;
        })
        .map(linha =>
            String(linha.CODIGO_INTEGRACAO)
        );

}
function sortLines(linhas = []) {
    return [...linhas].sort((a, b) =>
        String(a.NOMELINHA).localeCompare(
            String(b.NOMELINHA),
            'pt-BR'
        )
    );
}

function getSentidosSelecionados() {

    try {

        const saved = localStorage.getItem('sentidosSelecionados');

        return saved
            ? JSON.parse(saved)
            : sentidoOptions.map(i => i.id);

    } catch {
        return sentidoOptions.map(i => i.id);
    }
}

function saveSentidosSelecionados(sentidosSelecionados) {

    try {

        localStorage.setItem('sentidosSelecionados',
            JSON.stringify(sentidosSelecionados)
        );
        
    } catch {}

}

function getLinhasSelecionadas() {
    
    try {

        const saved = localStorage.getItem('linhasSelecionadas');

        return saved
            ? JSON.parse(saved)
            : null;

    } catch {
        return null;
    }

}

function saveLinhasSelecionadas(linhasSelecionadas) {
    
    try {

        localStorage.setItem('linhasSelecionadas',
            JSON.stringify(linhasSelecionadas)
        );

    } catch {}

}

function buildLinesState(linhas = []) {
    const sorted = sortLines(linhas);

    return {
        linhasDisponiveis: sorted,
        linhasDisponiveisMap: new Map(
            sorted.map(linha => [
                String(linha.CODIGO_INTEGRACAO),
                linha
            ])
        )
    };
}

function buildAllowLinhas(linhasSelecionadas) {

    return linhasSelecionadas === null
        ? null
        : new Set(
            (linhasSelecionadas || []).map(String)
        );

}

const initialLinhasSelecionadas = getLinhasSelecionadas();
const initialSentidosSelecionados = getSentidosSelecionados();

export const useLinesStore = create((set, get) => ({
    
    linesInitialized: false,

    linhasDisponiveis: [],
    linhasDisponiveisMap: new Map(),
    metrosTotalRotaMap: new Map(),

    linhasSelecionadas: initialLinhasSelecionadas,
    allowLinhas: buildAllowLinhas(initialLinhasSelecionadas),

    sentidoOptions,
    sentidosSelecionados: initialSentidosSelecionados,
    sentidosSet: new Set(initialSentidosSelecionados),

    setLinhas: (
        linhas = [],
        broadcast = true
    ) => {

        set({
            ...buildLinesState(linhas),
            linesInitialized: true
        });

        if (broadcast) {
            sendLines(linhas);
        }
    },

    setMetrosTotalRota: (
        metrosTotalRota = {},
        broadcast = true
    ) => {

        set((state) => {

            const map =
                new Map(
                    state.metrosTotalRotaMap
                );

            for (
                const [codigo, metros]
                of Object.entries(
                    metrosTotalRota
                )
            ) {

                map.set(
                    String(codigo),
                    Number(metros) || 0
                );

            }

            return {
                metrosTotalRotaMap: map
            };

        });

        if (broadcast) {

            sendMetrosTotalRota(metrosTotalRota);

        }

    },

    setMetrosTotalRotaLinha: (
        codigo,
        metrosTotalRota,
        broadcast = true
    ) => {

        const normalizedCodigo =
            String(
                codigo || ''
            ).trim();

        if (!normalizedCodigo) {
            return;
        }

        const normalizedMetros =
            Number(
                metrosTotalRota
            ) || 0;

        set((state) => {

            const map =
                new Map(
                    state.metrosTotalRotaMap
                );

            map.set(
                normalizedCodigo,
                normalizedMetros
            );

            return {
                metrosTotalRotaMap: map
            };

        });

        if (broadcast) {

            sendMetrosTotalRotaUpdated({
                codigo: normalizedCodigo,

                metrosTotalRota: normalizedMetros
            });

        }

    },

    removeMetrosTotalRotaLinha: (
        codigo,
        broadcast = true
    ) => {

        const normalizedCodigo =
            String(
                codigo || ''
            ).trim();

        if (!normalizedCodigo) {
            return;
        }

        set((state) => {

            const map =
                new Map(
                    state.metrosTotalRotaMap
                );

            map.delete(
                normalizedCodigo
            );

            return {
                metrosTotalRotaMap: map
            };

        });

        if (broadcast) {

            sendMetrosTotalRotaDeleted(normalizedCodigo);

        }

    },

    exportMetrosTotalRota: () => {

        return Object.fromEntries(
            get().metrosTotalRotaMap
        );

    },

    setLinhasSelecionadas: (linhasSelecionadas, broadcast = true) => {
        
        saveLinhasSelecionadas(linhasSelecionadas);

        set({ 
            linhasSelecionadas,
            allowLinhas: buildAllowLinhas(linhasSelecionadas) 
        });

        if (broadcast) {
            sendLinesFilters({
                linhasSelecionadas,
                sentidosSelecionados: get().sentidosSelecionados
            });
        }
    },

    setSentidosSelecionados: (sentidosSelecionados, broadcast = true) => {

        saveSentidosSelecionados(sentidosSelecionados);

        set({
            sentidosSelecionados,
            sentidosSet: new Set(sentidosSelecionados)
        });

        if (broadcast) {
            sendLinesFilters({
                linhasSelecionadas: get().linhasSelecionadas,
                sentidosSelecionados
            });
        }
    },

    setLinesFilters: (payload = {}) => {

        const next = {
            linhasSelecionadas:
                'linhasSelecionadas' in payload
                    ? payload.linhasSelecionadas
                    : get().linhasSelecionadas,

            sentidosSelecionados:
                'sentidosSelecionados' in payload
                    ? payload.sentidosSelecionados
                    : get().sentidosSelecionados
        };

        saveSentidosSelecionados(next.sentidosSelecionados);
        saveLinhasSelecionadas(next.linhasSelecionadas);

        set({
            ...next,
            allowLinhas: buildAllowLinhas(next.linhasSelecionadas),
            sentidosSet: new Set(next.sentidosSelecionados)
        });

    },

    upsertLinha: (linha, broadcast = true) => {
        if (!linha?.CODIGO_INTEGRACAO) {
            return;
        }

        set((state) => {
            const map = new Map(state.linhasDisponiveisMap);

            map.set(
                String(linha.CODIGO_INTEGRACAO),
                linha
            );

            return buildLinesState(
                Array.from(map.values())
            );
        });

        if (broadcast) {
            sendLineUpsert(linha);
        }
    },

    removeLinha: (codigo, broadcast = true) => {
        if (!codigo) {
            return;
        }

        set((state) => {
            const map = new Map(state.linhasDisponiveisMap);

            map.delete(String(codigo));

            return buildLinesState(
                Array.from(map.values())
            );
        });

        if (broadcast) {
            sendLineRemove(codigo);
        }
    },

    resetLines: (broadcast = false) => {

        set({
            linhasDisponiveis: [],
            linhasDisponiveisMap: new Map(),
            metrosTotalRotaMap: new Map(),
            linesInitialized: false
        });

        if (broadcast) {
            sendLines([]);
        }

    }

}));